import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUsers, getRoles, createUser, updateUser, deleteUser, uploadAvatar } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Search, Edit, Trash2, Mail, Phone, Users as UsersIcon, Plus, Filter, Camera } from 'lucide-react';

export default function Users() {
  const { hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '', password: '', full_name: '', phone: '', address: '', ci: '',
    role_id: '', is_active: true, birthday: '', categoria: 'adulto', es_menor: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([getUsers(), getRoles()]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditUser(user);
    setEditForm({
      full_name: user.full_name,
      phone: user.phone || '',
      address: user.address || '',
      ci: user.ci || '',
      role_id: user.role_id || '',
      is_active: user.is_active,
      birthday: user.birthday || '',
      categoria: user.categoria || 'adulto'
    });
  };

  const handleSaveUser = async () => {
    try {
      await updateUser(editUser.id, editForm);
      toast.success('Usuario actualizado');
      setEditUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editUser) return;
    setUploadingAvatar(true);
    try {
      const res = await uploadAvatar(file);
      setEditUser(prev => ({ ...prev, avatar_url: res.data.url }));
      toast.success('Foto actualizada');
      fetchData();
    } catch {
      toast.error('Error al subir foto');
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.email || !createForm.password || !createForm.full_name) {
      return toast.error('Email, contraseña y nombre son requeridos');
    }
    try {
      await createUser(createForm);
      toast.success('Usuario creado');
      setShowCreateUser(false);
      setCreateForm({ email: '', password: '', full_name: '', phone: '', address: '', ci: '',
        role_id: '', is_active: true, birthday: '', categoria: 'adulto', es_menor: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    try {
      await deleteUser(userId);
      toast.success('Usuario eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role?.nombre || role?.name || 'Sin rol';
  };

  const getRoleBadgeColor = (roleName) => {
    const colors = {
      'Superadmin': 'bg-red-100 text-red-700',
      'Presidente': 'bg-purple-100 text-purple-700',
      'Vicepresidente': 'bg-purple-50 text-purple-600',
      'Secretario': 'bg-blue-100 text-blue-700',
      'Tesorero': 'bg-emerald-100 text-emerald-700',
      'Vocal': 'bg-amber-100 text-amber-700',
      'Delegado': 'bg-cyan-100 text-cyan-700',
      'Integrante_Adulto': 'bg-stone-100 text-stone-700',
      'Integrante_Joven': 'bg-sky-100 text-sky-700',
      'Integrante_Nino': 'bg-pink-100 text-pink-700',
      'Patrocinador': 'bg-yellow-100 text-yellow-700',
    };
    return colors[roleName] || 'bg-stone-100 text-stone-700';
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !filterRole || user.role_id === filterRole;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="users-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">
            Gestión de Usuarios
          </h1>
          <p className="text-muted-foreground">
            {users.length} miembros registrados
          </p>
        </div>
        {hasPermission('usuarios', 'crear') && (
          <Button onClick={() => setShowCreateUser(true)} data-testid="create-user-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Usuario
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <UsersIcon className="w-5 h-5" />
              Lista de Miembros
            </CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar usuarios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="users-search"
              />
            </div>
            <Select value={filterRole || 'all'} onValueChange={v => setFilterRole(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]" data-testid="users-filter-role">
                <SelectValue placeholder="Todos los roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los roles</SelectItem>
                {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.nombre || r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Estado</TableHead>
                  {hasPermission('usuarios', 'editar') && <TableHead className="text-right">Acciones</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} className="table-row-hover">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={user.avatar_url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}` : user.avatar_url} />
                          <AvatarFallback className="bg-emerald-100 text-emerald-700">
                            {getInitials(user.full_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{user.full_name}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {user.phone && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Phone className="w-3 h-3" />
                            {user.phone}
                          </div>
                        )}
                        {user.email && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Mail className="w-3 h-3" />
                            {user.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getRoleBadgeColor(getRoleName(user.role_id))}>
                        {getRoleName(user.role_id)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.is_active ? 'default' : 'secondary'}>
                        {user.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    {hasPermission('usuarios', 'editar') && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditUser(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteUser(user.id)}
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Avatar Upload */}
            <div className="flex items-center gap-4" data-testid="edit-user-avatar-section">
              <div className="relative group">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={editUser?.avatar_url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${editUser.avatar_url}` : editUser?.avatar_url} />
                  <AvatarFallback className="bg-emerald-100 text-emerald-700 text-lg">
                    {editUser?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <Camera className="w-5 h-5 text-white" />
                  <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} data-testid="avatar-upload-input" />
                </label>
              </div>
              <div>
                <p className="font-medium text-sm">{editUser?.full_name}</p>
                <p className="text-xs text-muted-foreground">{uploadingAvatar ? 'Subiendo foto...' : 'Pasa el cursor sobre la foto para cambiarla'}</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nombre Completo</Label>
              <Input
                value={editForm.full_name || ''}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                data-testid="edit-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input
                value={editForm.phone || ''}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                data-testid="edit-user-phone"
              />
            </div>
            <div className="space-y-2">
              <Label>Dirección</Label>
              <Input
                value={editForm.address || ''}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>CI</Label>
              <Input
                value={editForm.ci || ''}
                onChange={(e) => setEditForm({ ...editForm, ci: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select
                value={editForm.role_id || ''}
                onValueChange={(value) => setEditForm({ ...editForm, role_id: value })}
              >
                <SelectTrigger data-testid="edit-user-role">
                  <SelectValue placeholder="Seleccionar rol" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.nombre || role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label>Estado:</Label>
              <Select
                value={editForm.is_active ? 'active' : 'inactive'}
                onValueChange={(value) => setEditForm({ ...editForm, is_active: value === 'active' })}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="inactive">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveUser} data-testid="save-user-btn">
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre Completo *</Label>
                <Input value={createForm.full_name} onChange={e => setCreateForm({...createForm, full_name: e.target.value})} data-testid="create-user-name" />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={createForm.email} onChange={e => setCreateForm({...createForm, email: e.target.value})} data-testid="create-user-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contraseña *</Label>
                <Input type="password" value={createForm.password} onChange={e => setCreateForm({...createForm, password: e.target.value})} data-testid="create-user-password" />
              </div>
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select value={createForm.role_id || 'none'} onValueChange={v => setCreateForm({...createForm, role_id: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="create-user-role"><SelectValue placeholder="Seleccionar rol" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin rol</SelectItem>
                    {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.nombre || r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={createForm.phone} onChange={e => setCreateForm({...createForm, phone: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>CI</Label>
                <Input value={createForm.ci} onChange={e => setCreateForm({...createForm, ci: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha de Nacimiento</Label>
                <Input type="date" value={createForm.birthday} onChange={e => setCreateForm({...createForm, birthday: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select value={createForm.categoria} onValueChange={v => setCreateForm({...createForm, categoria: v, es_menor: v === 'nino'})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="adulto">Adulto</SelectItem>
                    <SelectItem value="joven">Joven</SelectItem>
                    <SelectItem value="nino">Niño</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Dirección</Label>
              <Input value={createForm.address} onChange={e => setCreateForm({...createForm, address: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)}>Cancelar</Button>
            <Button onClick={handleCreateUser} data-testid="submit-create-user-btn">Crear Usuario</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
